// src/components/RequireSubscription.tsx
import React, { useState, useEffect, useCallback, JSX } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import api from '../services/api';

interface RequireSubscriptionProps {
    children: JSX.Element;
}

interface SubscriptionStatus {
    plan: any;
    trial_used: boolean;
}

const RequireSubscription: React.FC<RequireSubscriptionProps> = ({ children }) => {
    const { user, isAdmin, isSuperAdmin } = useUser();
    const location = useLocation();
    const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    // Define allowed paths for users without subscription
    const allowedPaths = [
        '/help',
        '/user-settings',
        '/subscriptions',
        '/subscription/success',
        '/subscription/cancelled'
    ];

    const fetchSubscriptionStatus = useCallback(async () => {
        if (!user) {
            setLoading(false);
            return;
        }

        // Only check subscription status for admin users
        // Candidates don't have subscriptions and should always be allowed
        // Super admins bypass subscription requirements
        if (!isAdmin() || isSuperAdmin()) {
            setSubscriptionStatus({ plan: null, trial_used: false });
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(false);
            const response = await api.get('subscriptions/me/');
            setSubscriptionStatus(response.data);
        } catch (err) {
            console.error('Error fetching subscription status:', err);
            setError(true);
            setSubscriptionStatus(null);
        } finally {
            setLoading(false);
        }
    }, [user, isAdmin, isSuperAdmin]);

    useEffect(() => {
        fetchSubscriptionStatus();
    }, [fetchSubscriptionStatus]);

    // Show loading while fetching subscription status
    if (loading) {
        return <div>Loading...</div>;
    }

    // If there's an error fetching subscription, allow access (fail open)
    if (error) {
        return children;
    }

    // Check if current path is allowed for users without subscription
    const isAllowedPath = allowedPaths.some(path => location.pathname.startsWith(path));

    // If user is on an allowed path, render the component
    if (isAllowedPath) {
        return children;
    }

    // Candidates don't need subscription requirements - only admins do
    // Super admins bypass subscription requirements
    if (!isAdmin() || isSuperAdmin()) {
        return children;
    }

    // For regular admin users: Check subscription requirements
    const hasNoSubscriptionHistory = !subscriptionStatus ||
        (subscriptionStatus.plan === null && !subscriptionStatus.trial_used);

    // If admin user has never had a subscription/trial, redirect to subscriptions page
    if (hasNoSubscriptionHistory) {
        return <Navigate to="/subscriptions" replace />;
    }

    // Admin user has had a trial or subscription before, allow access
    return children;
};

export default RequireSubscription;